import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Label,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Badge,
  Table,
} from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { baseUrl } from "../../Network";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { handleAuthError } from "../../utils";
import { isMarketerReadOnly } from "../../helpers/adminRole";
import withAuth from "../withAuth";
import { LearningPathIcon, LEARNING_PATH_ICONS } from "./LearningPathMetaFields";
import { learningPathErrorMessage, LearningPathErrorAlert } from "./learningPathErrors";

const emptyCategory = () => ({ title: "", icon: "code", example: "", sortOrder: 0 });
const emptySub = () => ({ title: "", icon: "", sortOrder: 0 });

const ManageAiLearningPathCategories = () => {
  document.title = "Learning Path Categories | RYD Admin";
  const readOnly = isMarketerReadOnly();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [listError, setListError] = useState("");

  const [categoryModal, setCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryForm, setCategoryForm] = useState(emptyCategory());
  const [savingCategory, setSavingCategory] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  const [subModal, setSubModal] = useState(false);
  const [parentCategory, setParentCategory] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState(emptySub());
  const [savingSub, setSavingSub] = useState(false);
  const [subError, setSubError] = useState("");

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setListError("");
      const response = await axios.get(`${baseUrl}/admin/learning-path/category/all`);
      setCategories(response?.data?.data || []);
    } catch (error) {
      handleAuthError(error);
      setListError(learningPathErrorMessage(error, "Couldn't load categories."));
      toast.error("Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryForm(emptyCategory());
    setCategoryError("");
    setCategoryModal(true);
  };

  const openEditCategory = (cat) => {
    setEditingCategory(cat);
    setCategoryForm({
      title: cat.title || "",
      icon: cat.icon || "code",
      example: cat.example || "",
      sortOrder: cat.sortOrder ?? 0,
    });
    setCategoryError("");
    setCategoryModal(true);
  };

  const saveCategory = async () => {
    if (!categoryForm.title.trim()) {
      setCategoryError("Title is required.");
      return;
    }
    try {
      setSavingCategory(true);
      setCategoryError("");
      const payload = {
        title: categoryForm.title.trim(),
        icon: categoryForm.icon || "code",
        example: categoryForm.example.trim() || null,
        sortOrder: Number(categoryForm.sortOrder) || 0,
      };
      if (editingCategory) {
        await axios.put(`${baseUrl}/admin/learning-path/category/${editingCategory.id}`, payload);
        toast.success("Category updated");
      } else {
        await axios.post(`${baseUrl}/admin/learning-path/category`, payload);
        toast.success("Category created");
      }
      setCategoryModal(false);
      fetchCategories();
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, "Couldn't save the category.");
      setCategoryError(message);
      toast.error(message);
    } finally {
      setSavingCategory(false);
    }
  };

  const openCreateSub = (cat) => {
    setParentCategory(cat);
    setEditingSub(null);
    setSubForm(emptySub());
    setSubError("");
    setSubModal(true);
  };

  const openEditSub = (cat, sub) => {
    setParentCategory(cat);
    setEditingSub(sub);
    setSubForm({
      title: sub.title || "",
      icon: sub.icon || "",
      sortOrder: sub.sortOrder ?? 0,
    });
    setSubError("");
    setSubModal(true);
  };

  const saveSub = async () => {
    if (!subForm.title.trim()) {
      setSubError("Title is required.");
      return;
    }
    if (!parentCategory) return;
    try {
      setSavingSub(true);
      setSubError("");
      const payload = {
        title: subForm.title.trim(),
        icon: subForm.icon || null,
        sortOrder: Number(subForm.sortOrder) || 0,
      };
      if (editingSub) {
        await axios.put(`${baseUrl}/admin/learning-path/subcategory/${editingSub.id}`, payload);
        toast.success("Subcategory updated");
      } else {
        await axios.post(`${baseUrl}/admin/learning-path/category/${parentCategory.id}/subcategory`, payload);
        toast.success("Subcategory created");
      }
      setSubModal(false);
      fetchCategories();
    } catch (error) {
      handleAuthError(error);
      const message = learningPathErrorMessage(error, "Couldn't save the subcategory.");
      setSubError(message);
      toast.error(message);
    } finally {
      setSavingSub(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      if (deleteTarget.type === "category") {
        await axios.delete(`${baseUrl}/admin/learning-path/category/${deleteTarget.id}`);
        toast.success("Category deleted");
      } else {
        await axios.delete(`${baseUrl}/admin/learning-path/subcategory/${deleteTarget.id}`);
        toast.success("Subcategory deleted");
      }
      setDeleteTarget(null);
      fetchCategories();
    } catch (error) {
      handleAuthError(error);
      toast.error(learningPathErrorMessage(error, "Couldn't delete."));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="AI Tools" breadcrumbItem="Learning Path Categories" />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                  <div>
                    <p className="text-muted mb-1">
                      Build the browse tree used by the AI course browser: <strong>Category → Subcategory → Learning path</strong>.
                    </p>
                    <Link to="/ai/learning-paths" className="small">← Back to learning paths</Link>
                  </div>
                  {!readOnly && (
                    <Button color="primary" onClick={openCreateCategory}>
                      <i className="mdi mdi-plus me-1" /> New category
                    </Button>
                  )}
                </div>
                <LearningPathErrorAlert message={listError} onDismiss={() => setListError("")} />
                {loading ? (
                  <div className="text-center py-5">Loading...</div>
                ) : !categories.length ? (
                  <div className="text-center text-muted py-5">
                    No categories yet. Create one like &quot;Coding&quot;, then add subcategories such as JavaScript and Python.
                  </div>
                ) : (
                  categories.map((cat) => (
                    <Card key={cat.id} className="border mb-3 shadow-none">
                      <CardBody className="py-3">
                        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-2">
                          <div className="d-flex align-items-center gap-2">
                            <LearningPathIcon icon={cat.icon || "code"} />
                            <div>
                              <div className="fw-semibold">{cat.title}</div>
                              <div className="text-muted small">
                                {cat.slug}
                                {cat.example ? ` · e.g. ${cat.example}` : ""}
                                {" · "}
                                <Badge color={cat.isVisible ? "success" : "secondary"} pill>
                                  {cat.isVisible ? "Visible" : "Hidden"}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          {!readOnly && (
                            <div className="d-flex flex-wrap gap-2">
                              <Button size="sm" color="info" outline onClick={() => openEditCategory(cat)}>Edit</Button>
                              <Button size="sm" color="primary" outline onClick={() => openCreateSub(cat)}>Add subcategory</Button>
                              <Button
                                size="sm"
                                color="danger"
                                outline
                                onClick={() => setDeleteTarget({ type: "category", id: cat.id, title: cat.title })}
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </div>
                        <div className="table-responsive">
                          <Table size="sm" className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th>Subcategory</th>
                                <th>Slug</th>
                                <th>Order</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(cat.subcategories || []).map((sub) => (
                                <tr key={sub.id}>
                                  <td className="fw-medium">{sub.title}</td>
                                  <td className="text-muted small">{sub.slug}</td>
                                  <td>{sub.sortOrder}</td>
                                  <td>
                                    {!readOnly && (
                                      <div className="d-flex flex-wrap gap-2">
                                        <Button size="sm" color="info" outline onClick={() => openEditSub(cat, sub)}>Edit</Button>
                                        <Button
                                          size="sm"
                                          color="danger"
                                          outline
                                          onClick={() => setDeleteTarget({ type: "subcategory", id: sub.id, title: sub.title })}
                                        >
                                          Delete
                                        </Button>
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              ))}
                              {!(cat.subcategories || []).length && (
                                <tr>
                                  <td colSpan={4} className="text-muted text-center py-3">
                                    No subcategories yet
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </Table>
                        </div>
                      </CardBody>
                    </Card>
                  ))
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal isOpen={categoryModal} toggle={() => !savingCategory && setCategoryModal(false)}>
        <ModalHeader toggle={() => !savingCategory && setCategoryModal(false)}>
          {editingCategory ? "Edit category" : "New category"}
        </ModalHeader>
        <ModalBody>
          <LearningPathErrorAlert message={categoryError} onDismiss={() => setCategoryError("")} />
          <div className="mb-3">
            <Label>Title *</Label>
            <Input
              value={categoryForm.title}
              onChange={(e) => setCategoryForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. Coding"
              disabled={savingCategory}
              maxLength={191}
            />
          </div>
          <div className="mb-3">
            <Label>Example hint</Label>
            <Input
              value={categoryForm.example}
              onChange={(e) => setCategoryForm((f) => ({ ...f, example: e.target.value }))}
              placeholder='e.g. "arrays", "Rust"'
              disabled={savingCategory}
              maxLength={191}
            />
          </div>
          <div className="mb-3">
            <Label>Icon</Label>
            <Input
              type="select"
              value={categoryForm.icon}
              onChange={(e) => setCategoryForm((f) => ({ ...f, icon: e.target.value }))}
              disabled={savingCategory}
            >
              {LEARNING_PATH_ICONS.map((i) => (
                <option key={i.value} value={i.value}>{i.label}</option>
              ))}
            </Input>
          </div>
          <div className="mb-3">
            <Label>Sort order</Label>
            <Input
              type="number"
              min={0}
              value={categoryForm.sortOrder}
              onChange={(e) => setCategoryForm((f) => ({ ...f, sortOrder: Number(e.target.value || 0) }))}
              disabled={savingCategory}
            />
          </div>
          <div className="d-flex justify-content-end">
            <Button color="secondary" className="me-2" disabled={savingCategory} onClick={() => setCategoryModal(false)}>Cancel</Button>
            <Button color="primary" disabled={savingCategory} onClick={saveCategory}>
              {savingCategory ? <><Spinner size="sm" className="me-2" />Saving…</> : "Save"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={subModal} toggle={() => !savingSub && setSubModal(false)}>
        <ModalHeader toggle={() => !savingSub && setSubModal(false)}>
          {editingSub ? "Edit subcategory" : `New subcategory under ${parentCategory?.title || ""}`}
        </ModalHeader>
        <ModalBody>
          <LearningPathErrorAlert message={subError} onDismiss={() => setSubError("")} />
          <div className="mb-3">
            <Label>Title *</Label>
            <Input
              value={subForm.title}
              onChange={(e) => setSubForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g. JavaScript"
              disabled={savingSub}
              maxLength={191}
            />
          </div>
          <div className="mb-3">
            <Label>Sort order</Label>
            <Input
              type="number"
              min={0}
              value={subForm.sortOrder}
              onChange={(e) => setSubForm((f) => ({ ...f, sortOrder: Number(e.target.value || 0) }))}
              disabled={savingSub}
            />
          </div>
          <div className="d-flex justify-content-end">
            <Button color="secondary" className="me-2" disabled={savingSub} onClick={() => setSubModal(false)}>Cancel</Button>
            <Button color="primary" disabled={savingSub} onClick={saveSub}>
              {savingSub ? <><Spinner size="sm" className="me-2" />Saving…</> : "Save"}
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={!!deleteTarget} toggle={() => !deleting && setDeleteTarget(null)}>
        <ModalHeader toggle={() => !deleting && setDeleteTarget(null)}>Confirm delete</ModalHeader>
        <ModalBody>
          <p>
            Delete {deleteTarget?.type} <strong>{deleteTarget?.title}</strong>?
            {deleteTarget?.type === "category"
              ? " Its subcategories will be removed and any attached learning paths will be unassigned."
              : " Attached learning paths will be unassigned (not deleted)."}
          </p>
          <div className="d-flex justify-content-end">
            <Button color="secondary" className="me-2" disabled={deleting} onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="danger" disabled={deleting} onClick={confirmDelete}>
              {deleting ? <><Spinner size="sm" className="me-2" />Deleting…</> : "Delete"}
            </Button>
          </div>
        </ModalBody>
      </Modal>
      <ToastContainer />
    </div>
  );
};

export default withAuth(ManageAiLearningPathCategories);
